import { AxiosError, AxiosResponse } from 'axios';
import queryString from 'query-string';
import { useContext } from 'react';

import AppContext from '@/contexts/appContext';
import {
  AvailableSlotsResponse,
  BookingParams,
  GetAvailableSlotsParams
} from '@/pages/Booking/types';
import { AppointmentInsights } from '@/types/appointmentAttendance';
import {
  Appointment,
  AppointmentAttendance,
  SyncUserAppointment,
  SyncUserAppointmentResponse
} from '@/types/appointments';
import { AppointmentType } from '@/types/appointmentType';
import PaginatedResponse from '@/types/paginatedResponse';
import { QueryParams } from '@/types/query';
import RepositoryResponse from '@/types/repositoryResponse';
import { ScheduleItem } from '@/types/scheduleItem';
import ajax from '@/utils/ajax';
import logAjaxError from '@/utils/logAjaxError';

export interface GetAppointmentsParams {
  appointment__appointment_type__clinical?: string;
  appointment__appointment_type__counselor_1_vs_1?: string;
  appointment__appointment_type__drug_screen_expected?: string;
  appointment__appointment_type__reward_as_group_meeting?: string;
  appointment__appointment_type__initial_counselor_1_vs_1?: string;
  appointment__appointment_type__is_continued_intake?: string;
  appointment__appointment_type__is_initial_medical_check?: string;
  appointment__appointment_type__is_intake?: string;
  appointment__appointment_type__is_medical_check?: string;
  appointment__appointment_type__name?: string;
  appointment__end_at__gte?: Date;
  appointment__end_at__lte?: Date;
  appointment__program_adherence?: boolean;
  appointment__start_at__gte?: Date;
  appointment__start_at__lte?: Date;
  appointment_id?: string;
  host?: string;
  order_by?: string;
  page?: number;
  past?: boolean;
  person_id?: string;
  size?: number;
  state?: string;
  state__in?: string;
  upcoming?: boolean;
}

export interface GetCalendarAppointmentsParams {
  start?: string; // ISO format datetime
  end?: string; // ISO format datetime
}

export interface GetBookingParamsParams {
  start_date: string; // ISO format datetime
  end_date: string; // ISO format datetime
  appointment_type_id: string; // Healthie appointment type ID
  person_id: string; // Person UUID
}

export interface GetScheduleItemsParams {
  personId: string;
  startAt?: Date;
  endAt?: Date;
  objectType?: 'attendance' | 'expectation_cycle';
  attendanceStates?: string[];
}

export const appointmentsRepository = {
  async sendScheduleSummaryEmail({
    host,
    person_id,
    rangeStartOn,
    rangeEndOn
  }: {
    host: string;
    person_id: string;
    rangeStartOn: string;
    rangeEndOn: string;
  }): Promise<{ success: boolean; message?: string }> {
    try {
      const path = '/api/appointment_summary/send_email_summary/';
      const response = await ajax.get({
        url: `${host}${path}`,
        params: {
          person_id,
          rangeStartOn,
          rangeEndOn
        }
      });
      return {
        success: true,
        message: response.data?.detail
      };
    } catch (error) {
      logAjaxError(error);
      const errResponse = (error as AxiosError).response;
      let message: string | undefined = undefined;
      const data = errResponse?.data;
      if (data && typeof data === 'object' && 'detail' in data) {
        message = (data as { detail?: string }).detail;
      } else if (typeof data === 'string') {
        message = data;
      }
      return {
        success: false,
        message
      };
    }
  },
  async getAppointment({
    host,
    appointmentId
  }: {
    host: string;
    appointmentId: string;
  }): Promise<Appointment> {
    const path = `/api/dugout/appointments/${appointmentId}/`;
    const response = await ajax.get({
      url: `${host}${path}`
    });

    return response.data;
  },

  async getAppointments(
    params: GetAppointmentsParams
  ): Promise<RepositoryResponse<PaginatedResponse<AppointmentAttendance[]>>> {
    const {
      appointment_id,
      appointment__start_at__gte,
      appointment__start_at__lte,
      appointment__end_at__gte,
      appointment__end_at__lte,
      appointment__appointment_type__name,
      appointment__program_adherence,
      host,
      order_by,
      page,
      past,
      person_id,
      size,
      state,
      upcoming
    } = params;
    try {
      // [NOTE]: Although this endpoint is called /appointments, it actually is fetching
      // a list of appointment_attendances, not appointments.
      const path = '/api/dugout/appointments/';
      const response = await ajax.get({
        url: `${host}${path}`,
        params: {
          ...params,
          member: person_id,
          appointment_id,
          appointment__start_at__gte: appointment__start_at__gte?.toISOString(),
          appointment__start_at__lte: appointment__start_at__lte?.toISOString(),
          appointment__end_at__gte: appointment__end_at__gte?.toISOString(),
          appointment__end_at__lte: appointment__end_at__lte?.toISOString(),
          upcoming,
          past,
          order_by: order_by,
          appointment__appointment_type__name,
          appointment__program_adherence,
          state,
          page,
          size
        }
      });

      return {
        data: response.data,
        isError: false
      };
    } catch (error) {
      logAjaxError(error);

      return {
        errorMessage: `Failed to get appointments: ${(error as AxiosError).message}`,
        error: error as AxiosError,
        isError: true
      };
    }
  },

  async getScheduleItems({
    host,
    personId,
    startAt,
    endAt,
    objectType,
    attendanceStates
  }: {
    host: string;
  } & GetScheduleItemsParams): Promise<ScheduleItem[]> {
    const path = `/api/appointments/schedule_items/${personId}/`;
    const response = await ajax.get({
      url: `${host}${path}`,
      params: {
        start_at: startAt?.toISOString(),
        end_at: endAt?.toISOString(),
        object_type: objectType,
        attendance_states: attendanceStates
      },
      config: {
        // attendance_states must be repeated keys (attendance_states=a&attendance_states=b) to
        // match Django's request.GET.getlist() -- the default paramsSerializer comma-joins arrays.
        paramsSerializer: {
          serialize: (params) => queryString.stringify(params, { arrayFormat: 'none' })
        }
      }
    });

    return response.data;
  },

  async getCalendarAppointments({
    host,
    params
  }: {
    host: string;
    params: GetCalendarAppointmentsParams;
  }): Promise<Appointment[]> {
    const path = '/api/dugout/appointments/calendar/';
    const response = await ajax.get({
      url: `${host}${path}`,
      params: {
        start: params.start,
        end: params.end
      }
    });

    return response.data;
  },

  async getAppointmentTypes({
    host,
    params
  }: {
    host: string;
    params?: QueryParams;
  }): Promise<AppointmentType[]> {
    const path = `/api/dugout/appointment_types/appointment_type_options`;
    const response = await ajax.get({
      url: `${host}${path}`,
      params
    });

    return response.data;
  },

  async getAppointmentAttendanceInsights({
    host,
    personId
  }: {
    host: string;
    personId: string;
  }): Promise<AppointmentInsights> {
    const path = `/api/dugout/appointments/appointment_insights/`;
    const response = await ajax.get({
      url: `${host}${path}`,
      params: { person_id: personId }
    });

    return response.data;
  },

  async cancelAppointments({
    host,
    appointmentIds
  }: {
    host: string;
    appointmentIds: string[];
  }): Promise<string[]> {
    const path = `/api/dugout/appointments/cancel_appointments/`;
    const response = await ajax.post<
      { appointment_attendance_ids: string[] },
      AxiosResponse<{ cancelled_appointments: string[] }>
    >({
      url: `${host}${path}`,
      data: { appointment_attendance_ids: appointmentIds }
    });

    return response.data.cancelled_appointments;
  },

  async getJoinLink({
    host,
    attendanceId
  }: {
    host: string;
    attendanceId: string;
  }): Promise<string> {
    const path = `/api/dugout/appointments/${attendanceId}/join_link/`;
    const response = await ajax.get({
      url: `${host}${path}`
    });

    return response.data.join_link;
  },

  async getBookingParams({
    host,
    params
  }: {
    host: string;
    params: GetBookingParamsParams;
  }): Promise<BookingParams> {
    const path = `/api/dugout/appointments/booking-params`;
    const response = await ajax.get({
      url: `${host}${path}`,
      params: {
        start_date: params.start_date,
        end_date: params.end_date,
        appointment_type_id: params.appointment_type_id,
        person_id: params.person_id
      }
    });

    return response.data;
  },

  async syncAppointment({
    host,
    body
  }: {
    host: string;
    body: SyncUserAppointment;
  }): Promise<SyncUserAppointmentResponse> {
    const path = `/api/appointments/sync-new-appointment/`;
    const response = await ajax.post<
      SyncUserAppointment,
      AxiosResponse<SyncUserAppointmentResponse>
    >({
      url: `${host}${path}`,
      data: body
    });

    return response.data;
  },
  async createAppointment({
    host,
    body
  }: {
    host: string;
    body: Record<string, unknown>;
  }): Promise<Appointment> {
    const path = `/api/appointments/create/`;
    const response = await ajax.post<Record<string, unknown>, AxiosResponse<Appointment>>({
      url: `${host}${path}`,
      data: body
    });

    return response.data;
  },

  async getAvailableSlots({
    host,
    params
  }: {
    host: string;
    params: GetAvailableSlotsParams;
  }): Promise<RepositoryResponse<AvailableSlotsResponse>> {
    try {
      const path = '/api/slots/';
      const response = await ajax.get({
        url: `${host}${path}`,
        params: {
          staff_ids: params.staff_ids,
          appointment_type_id: params.appointment_type_id,
          start_on: params.start_on,
          end_on: params.end_on,
          timezone: params.timezone,
          jurisdiction_id: params.jurisdiction_id,
          appointment_duration_minutes: params.appointment_duration_minutes,
          appointment_to_reschedule_id: params.appointment_to_reschedule_id,
          person_id: params.person_id
        },
        config: {
          paramsSerializer: {
            indexes: true
          }
        }
      });

      return {
        data: response.data,
        isError: false
      };
    } catch (error) {
      logAjaxError(error);

      return {
        errorMessage: `Failed to get available slots: ${(error as AxiosError).message}`,
        error: error as AxiosError,
        isError: true
      };
    }
  }
};

const AppointmentsRepository = () => {
  const { host } = useContext(AppContext);

  return {
    async getAppointment(appointmentId: string): Promise<Appointment> {
      return appointmentsRepository.getAppointment({ host, appointmentId });
    },

    async getAppointments(
      args: Partial<GetAppointmentsParams>
    ): Promise<RepositoryResponse<PaginatedResponse<AppointmentAttendance[]>>> {
      return appointmentsRepository.getAppointments({ host, ...args });
    },

    async getAppointmentAttendanceInsights(personId: string): Promise<AppointmentInsights> {
      return appointmentsRepository.getAppointmentAttendanceInsights({ host, personId });
    },

    async cancelAppointments(appointmentIds: string[]): Promise<string[]> {
      return appointmentsRepository.cancelAppointments({ host, appointmentIds });
    },

    async getJoinLink(attendanceId: string): Promise<string> {
      return appointmentsRepository.getJoinLink({ host, attendanceId });
    },

    async getAppointmentTypes(params?: QueryParams): Promise<AppointmentType[]> {
      return appointmentsRepository.getAppointmentTypes({ host, params });
    },

    async getCalendarAppointments(params: GetCalendarAppointmentsParams): Promise<Appointment[]> {
      return appointmentsRepository.getCalendarAppointments({ host, params });
    },

    async getScheduleItems(params: GetScheduleItemsParams): Promise<ScheduleItem[]> {
      return appointmentsRepository.getScheduleItems({ host, ...params });
    },

    async getBookingParams(params: GetBookingParamsParams): Promise<BookingParams> {
      return appointmentsRepository.getBookingParams({ host, params });
    },

    async syncAppointment(body: SyncUserAppointment): Promise<SyncUserAppointmentResponse> {
      return appointmentsRepository.syncAppointment({ host, body });
    },

    async getAvailableSlots(
      params: GetAvailableSlotsParams
    ): Promise<RepositoryResponse<AvailableSlotsResponse>> {
      return appointmentsRepository.getAvailableSlots({ host, params });
    },

    async createAppointment(body: Record<string, unknown>): Promise<Appointment> {
      return appointmentsRepository.createAppointment({ host, body });
    },

    async sendScheduleSummaryEmail(
      person_id: string,
      rangeStartOn: string,
      rangeEndOn: string
    ): Promise<{ success: boolean; message?: string }> {
      return appointmentsRepository.sendScheduleSummaryEmail({
        host,
        person_id,
        rangeStartOn,
        rangeEndOn
      });
    }
  };
};

export default AppointmentsRepository;
